import {
  NgTemplateOutlet
} from '@angular/common';
import {
    HttpBackend,
    HttpClient,
} from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, computed, inject, signal, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    FormBuilder,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import {
    TranslocoPipe,
    TranslocoService,
} from '@jsverse/transloco';
import {
    UntilDestroy,
    untilDestroyed,
} from '@ngneat/until-destroy';
import { CountryCode } from 'libphonenumber-js';
import { NgxDropzoneModule } from 'ngx-dropzone';
import {
    filter,
    first,
    map,
    switchMap,
} from 'rxjs';

import { PERMISSIONS } from '~/app/domains/authorisation/constants/permissions.constants';
import { SnackbarService } from '~/app/shared/services/snackbar/snackbar.service';
import { AuthenticationFacade } from '~/app/core/state/authentication/authentication.facade';
import { UpdateCompanyArgs } from '~/app/core/state/authentication/authentication.actions';
import { BlurredContentComponent } from '~/app/shared/components/layouts/blurred-content/blurred-content.component';
import { BlurredContentBodyComponent } from '~/app/shared/components/layouts/blurred-content-body/blurred-content-body.component';
import { BlurredContentOverlayComponent } from '~/app/shared/components/layouts/blurred-content-overlay/blurred-content-overlay.component';
import { DropzoneImagePreviewComponent } from '~/app/shared/components/menu/dropzone-image-preview/dropzone-image-preview.component';
import { H2Component } from '~/app/shared/components/elements/h2/h2.component';
import { IconComponent } from '~/app/shared/components/elements/icon/icon.component';
import { LabelComponent } from '~/app/shared/components/elements/label/label.component';
import { MaterialInputSelectComponent } from '~/app/shared/components/forms/formElements/material-input-select/material-input-select.component';
import { MaterialInputSelectOptionComponent } from '~/app/shared/components/forms/formElements/material-input-select-option/material-input-select-option.component';
import { MaterialInputTextComponent } from '~/app/shared/components/forms/formElements/material-input-text/material-input-text.component';
import { HasPermissionsDirective } from '~/app/domains/authorisation/directives/has-permissions.directive';
import { BasePermissionsService } from '~/app/shared/services/base-permissions/base-permissions.service';
import { RESUME_FEATURES } from '~/app/shared/enums/resume-features.enum';
import { ModalImageCropperService } from '~/app/shared/services/modal/modal-image-cropper.service';
import { ModalService } from '~/app/shared/services/modal/modal.service';
import { ModalConfirmationV2Component } from '~/app/shared/modals/v2/modal-confirmation/modal-confirmation.component';
import { ModalRef } from '~/app/shared/services/modal/modal-ref';
import { Company } from '~/app/shared/types/company.type';
import { Address } from '~/app/shared/types/user/address.type';
import { DropzoneFileResult } from '~/app/shared/types/dropzone-fileresult.type';
import {
    ButtonWithIconComponent
} from "~/app/shared/components/elements/buttons/button-with-icon/button-with-icon.component";
import { buildProfileCountries, postalCodeValidatorForCountry } from '../../constants/countries.constant';

enum LOGO_TARGET {
    'MAIN' = 'MAIN',
    'SECOND' = 'SECOND'
}

/** Adresse « vide » servant de base quand la compagnie n'en a pas encore (préserve la forme du type). */
const EMPTY_ADDRESS: Address = {
    id: 0,
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
};

@UntilDestroy()
@Component({
    selector: 'eb-profile-company',
    host: { '[class.profile-company--embedded]': 'embedded()' },
    templateUrl: './profile-company.component.html',
    styleUrls: ['./profile-company.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    ReactiveFormsModule,
    H2Component,
    LabelComponent,
    HasPermissionsDirective,
    NgTemplateOutlet,
    BlurredContentComponent,
    BlurredContentBodyComponent,
    BlurredContentOverlayComponent,
    IconComponent,
    NgxDropzoneModule,
    DropzoneImagePreviewComponent,
    MaterialInputTextComponent,
    MaterialInputSelectComponent,
    MaterialInputSelectOptionComponent,
    TranslocoPipe,
    ButtonWithIconComponent
]
})
export class ProfileCompanyComponent implements OnInit, OnDestroy {
    private cd = inject(ChangeDetectorRef);
    private formBuilder = inject(FormBuilder);
    private authFacade = inject(AuthenticationFacade);
    private modalImageCropperService = inject(ModalImageCropperService);
    private modalService = inject(ModalService);
    private snackbarService = inject(SnackbarService);
    private translocoService = inject(TranslocoService);
    private permissionsService = inject(BasePermissionsService);

    public mainLogo: File | null = null;

    public secondaryLogo: File | null = null;

    /** Signaux (et non booléens simples) : consommés par le `computed` `canSave` en mode zoneless. */
    public readonly mainLogoChanged = signal(false);

    public readonly secondaryLogoChanged = signal(false);

    /** Données communes = éditables uniquement avec la permission de gestion (comme les logos). */
    /** Sans la permission, les données communes de la société sont en lecture seule. */
    public readonly canManage = this.permissionsService.hasPermissions([PERMISSIONS.PROFILE_LOGO_MANAGE]);

    /** EF-3852 — intégré à la popin : intitulé de section au lieu du grand titre, pas de bouton propre. */
    public readonly embedded = input(false);

    /** Émis une fois les modifications enregistrées (après confirmation). */
    public readonly saved = output<void>();

    public readonly countries = buildProfileCountries(this.translocoService.getActiveLang());

    public readonly form = this.formBuilder.nonNullable.group({
        companyName: ['', [Validators.required]],
        street: [''],
        postalCode: ['', [postalCodeValidatorForCountry(null)]],
        city: [''],
        country: ['' as CountryCode],
    });

    private readonly formValue = toSignal(this.form.valueChanges, { initialValue: this.form.getRawValue() });

    private readonly formValid = signal(this.form.valid);

    /**
     * Valeurs enregistrées (référence). Comparer la valeur courante à cette référence est robuste aux
     * ré-émissions des CVA (mêmes précautions que `profile-professional-info`) et sert de base au reset.
     */
    private readonly savedValue = signal(this.form.getRawValue());

    /** Adresse chargée : conservée pour préserver `id`, `addressLine2/3` et `state` non édités par le form. */
    private savedAddress: Address | null = null;

    private readonly hasFormChanges = computed(() => (
        JSON.stringify(this.formValue()) !== JSON.stringify(this.savedValue())
    ));

    /** Modifications en attente : un logo, ou le nom, ou l'adresse. */
    public readonly hasUnsavedChanges = computed(() => (
        this.mainLogoChanged() || this.secondaryLogoChanged() || this.hasFormChanges()
    ));

    /** Actif seulement si le formulaire est valide ET qu'il y a une modification. */
    public readonly canSave = computed(() => this.formValid() && this.hasUnsavedChanges());

    public readonly disabledExplanationKey = computed(() => (
        this.formValid()
            ? 'common.error.no_changes'
            : 'profile.company.required_fields_missing'
    ));

    public logoUrl$ = this.authFacade.userCompany$.pipe(
        filter((company: Company | undefined): company is Company => !!company),
        map((company) => company.logoUrl),
    );

    public squareLogoUrl$ = this.authFacade.userCompany$.pipe(
        filter((company: Company | undefined): company is Company => !!company),
        map((company) => company.squareLogoUrl),
    );

    protected readonly PERMISSIONS = PERMISSIONS;

    protected readonly RESUME_FEATURES = RESUME_FEATURES;

    protected readonly LOGO_TARGET = LOGO_TARGET;

    private http: HttpClient;

    private modalRef: ModalRef | null = null;

    constructor() {
        const httpBackend = inject(HttpBackend);

        this.http = new HttpClient(httpBackend);

        // Le CP dépend du pays : on revalide son format au changement de pays (même logique que la carte infos pro).
        this.form.controls.country.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe((country) => {
                const postalCode = this.form.controls.postalCode;
                postalCode.setValidators([postalCodeValidatorForCountry(country)]);
                postalCode.updateValueAndValidity();
                if (postalCode.invalid) {
                    postalCode.markAsDirty();
                }
            });

        this.form.statusChanges
            .pipe(untilDestroyed(this))
            .subscribe(() => this.formValid.set(this.form.valid));
    }

    public ngOnInit(): void {
        this.logoUrl$
            .pipe(
                first(),
                filter((logoUrl) => logoUrl !== null),
                switchMap((url: string) => this.http.get(url, {
                    responseType: 'arraybuffer',
                    observe: 'response',
                })),
                map((response) => {
                    if (!response.body) {
                        return null;
                    }
                    const blob = new Blob([response.body], { type: response.headers.get('content-type') ?? 'application/octet-stream' });
                    return new File([blob], 'file', { type: response.headers.get('content-type') ?? 'application/octet-stream' });
                }),
                untilDestroyed(this),
            )
            .subscribe((file: File | null) => {
                this.mainLogo = file;
                this.cd.markForCheck();
            });
        this.squareLogoUrl$
            .pipe(
                first(),
                filter((logoUrl) => logoUrl !== null),
                switchMap((url: string) => this.http.get(url, {
                    responseType: 'arraybuffer',
                    observe: 'response',
                })),
                map((response) => {
                    if (!response.body) {
                        return null;
                    }
                    const blob = new Blob([response.body], { type: response.headers.get('content-type') ?? 'application/octet-stream' });
                    return new File([blob], 'file', { type: response.headers.get('content-type') ?? 'application/octet-stream' });
                }),
                untilDestroyed(this),
            )
            .subscribe((file: File | null) => {
                this.secondaryLogo = file;
                this.cd.markForCheck();
            });

        this.authFacade.userCompany$
            .pipe(
                filter((company: Company | undefined): company is Company => !!company),
                first(),
                untilDestroyed(this),
            )
            .subscribe((company) => this.prefillCompany(company));

        this.authFacade.onUpdateCompanySuccess()
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.snackbarService.openSuccess(this.translocoService.translate('profile.changing_company_success'));
            });
    }

    public ngOnDestroy(): void {
        this.modalRef?.dispose();
    }

    onSelectLogoByTarget(event: DropzoneFileResult, target: LOGO_TARGET) {
        if (target === LOGO_TARGET.MAIN) {
            this.onSelectMainLogo(event);
            return;
        }
        this.onSelectSecondaryLogo(event);
    }

    onRemoveLogoByTarget(target: LOGO_TARGET) {
        if (target === LOGO_TARGET.MAIN) {
            this.mainLogo = null;
            this.mainLogoChanged.set(true);
        } else {
            this.secondaryLogo = null;
            this.secondaryLogoChanged.set(true);
        }
        this.cd.markForCheck();
    }

    onSelectMainLogo(event: DropzoneFileResult) {
        [this.mainLogo] = event.addedFiles;
        this.mainLogoChanged.set(true);
        this.cd.markForCheck();
    }

    onSelectSecondaryLogo(event: DropzoneFileResult) {
        this.modalRef = this.modalImageCropperService.open(event.addedFiles[0]);

        this.modalRef.afterClosed$
            .pipe(
                first(),
                untilDestroyed(this),
            )
            .subscribe((file: File | null | undefined) => {
                if (file !== undefined) {
                    this.secondaryLogo = file;
                    this.secondaryLogoChanged.set(true);
                    this.cd.markForCheck();
                }
            });
    }

    public saveChanges(): void {
        if (!this.canSave()) {
            return;
        }

        // Données communes → on demande confirmation avant d'impacter tous les membres de la compagnie.
        // La modal pilote elle-même le PATCH (`onConfirmation`) : spinner (`withLoading`) + alerte d'erreur
        // sans se fermer si l'appel échoue. `afterClosed$ === true` ne sert qu'à réinitialiser l'état local.
        this.modalRef = this.modalService.open<boolean>(
            ModalConfirmationV2Component,
            {
                icon: 'ui/label/notif',
                message: this.translocoService.translate('profile.company.update_confirmation.message'),
                descriptionSentences: [this.translocoService.translate('profile.company.update_confirmation.description')],
                withLoading: true,
                onConfirmation: () => this.authFacade.updateCompany(this.buildUpdateArgs()),
            },
        );

        this.modalRef.afterClosed$
            .pipe(first(), untilDestroyed(this))
            .subscribe((confirmed?: boolean) => {
                if (confirmed === true) {
                    this.resetAfterSave();
                }
            });
    }

    private prefillCompany(company: Company): void {
        this.savedAddress = company.address ?? null;
        this.form.patchValue({
            companyName: company.name ?? '',
            street: company.address?.addressLine1 ?? '',
            postalCode: company.address?.postalCode ?? '',
            city: company.address?.city ?? '',
            country: (company.address?.country ?? '') as CountryCode,
        });
        this.form.markAsPristine();
        this.savedValue.set(this.form.getRawValue());
        this.formValid.set(this.form.valid);
        // Sans permission : on montre les infos communes en lecture seule (champs désactivés).
        if (!this.canManage) {
            this.form.disable({ emitEvent: false });
        }
        this.cd.markForCheck();
    }

    private buildUpdateArgs(): UpdateCompanyArgs {
        const value = this.form.getRawValue();
        return {
            ...(this.mainLogoChanged() ? { logoFile: this.mainLogo } : {}),
            ...(this.secondaryLogoChanged() ? { squareLogoFile: this.secondaryLogo } : {}),
            ...(this.hasFormChanges() ? { name: value.companyName, address: this.buildAddress() } : {}),
        };
    }

    /** Reconstitue l'adresse à envoyer en préservant les champs non édités (`id`, `addressLine2/3`, `state`). */
    private buildAddress(): Address {
        const value = this.form.getRawValue();
        return {
            ...(this.savedAddress ?? EMPTY_ADDRESS),
            addressLine1: value.street,
            city: value.city,
            country: value.country,
            postalCode: value.postalCode,
        };
    }

    private resetAfterSave(): void {
        this.mainLogoChanged.set(false);
        this.secondaryLogoChanged.set(false);
        this.savedAddress = this.buildAddress();
        this.form.markAsPristine();
        this.savedValue.set(this.form.getRawValue());
        this.cd.markForCheck();
        this.saved.emit();
    }
}
